"use client";

import { useState, useEffect, useCallback } from "react";

const BP = process.env.NEXT_PUBLIC_BASE_PATH ?? "";

interface GalleryImage {
  id: string;
  nasPath: string;
  lookNumber: number | null;
  alt: string | null;
}

interface Props {
  images: GalleryImage[];
}

export default function ShowGallery({ images }: Props) {
  const [open, setOpen] = useState<number | null>(null);

  const close = useCallback(() => setOpen(null), []);
  const prev = useCallback(() => setOpen((i) => (i != null ? (i - 1 + images.length) % images.length : null)), [images.length]);
  const next = useCallback(() => setOpen((i) => (i != null ? (i + 1) % images.length : null)), [images.length]);

  useEffect(() => {
    if (open == null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, close, prev, next]);

  useEffect(() => {
    document.body.style.overflow = open != null ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const current = open != null ? images[open] : null;

  return (
    <>
      <div className="grid-gallery" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, background: "var(--ink)" }}>
        {images.map((image, index) => (
          <button
            key={image.id}
            onClick={() => setOpen(index)}
            style={{
              position: "relative",
              background: "var(--paper-2)",
              aspectRatio: "2/3",
              overflow: "hidden",
              border: "none",
              padding: 0,
              cursor: "zoom-in",
              display: "block",
              width: "100%",
            }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={`${BP}/api/thumb/${image.nasPath}?w=400`}
              alt={image.alt ?? `Look ${image.lookNumber ?? index + 1}`}
              loading="lazy"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
            {image.lookNumber && (
              <div style={{
                position: "absolute", bottom: 0, left: 0,
                padding: "6px 10px",
                fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em",
                background: "rgba(0,0,0,0.55)", color: "white",
                pointerEvents: "none",
              }}>
                {String(image.lookNumber).padStart(2, "0")}
              </div>
            )}
          </button>
        ))}
      </div>

      {current && (
        <div
          onClick={close}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.92)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {/* Prev */}
          <button
            className="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            style={{
              position: "absolute", left: 24, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "1px solid rgba(255,255,255,0.25)", color: "white",
              fontFamily: "var(--font-mono)", fontSize: 18, width: 48, height: 48,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >←</button>

          {/* Image */}
          <div onClick={(e) => e.stopPropagation()} style={{ maxHeight: "90vh", maxWidth: "80vw", position: "relative" }}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={`${BP}/api/thumb/${current.nasPath}?w=1200`}
              alt={current.alt ?? ""}
              style={{ maxHeight: "90vh", maxWidth: "80vw", objectFit: "contain", display: "block" }}
            />
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              padding: "10px 16px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              background: "rgba(0,0,0,0.6)",
              fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.1em", color: "rgba(255,255,255,0.7)",
            }}>
              {current.lookNumber != null
                ? <span>Look {String(current.lookNumber).padStart(2, "0")}</span>
                : <span />}
              <span>{(open ?? 0) + 1} / {images.length}</span>
            </div>
          </div>

          {/* Next */}
          <button
            className="lightbox-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            style={{
              position: "absolute", right: 24, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "1px solid rgba(255,255,255,0.25)", color: "white",
              fontFamily: "var(--font-mono)", fontSize: 18, width: 48, height: 48,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >→</button>

          {/* Close */}
          <button
            onClick={close}
            style={{
              position: "absolute", top: 20, right: 24,
              background: "none", border: "1px solid rgba(255,255,255,0.25)", color: "white",
              fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.1em",
              padding: "6px 12px", cursor: "pointer",
            }}
          >ESC</button>
        </div>
      )}
    </>
  );
}
